import React, { useState, useEffect } from 'react';
import { timeTrackingApi } from '../../services/timeTrackingApi';
import { FiCalendar, FiClock, FiTrendingUp, FiBarChart2, FiEdit3 } from 'react-icons/fi';
import api from '../../services/api';

const MonthlyReportPage = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [reportData, setReportData] = useState({
    dailyBreakdown: [],
    monthlyTotals: {
      totalDays: 0,
      workingDays: 0,
      totalHours: 0,
      activeHours: 0,
      idleHours: 0,
      manualHours: 0,
      productivity: 0
    },
    projectWiseData: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadMonthlyReport();
  }, [selectedMonth]);

  const loadMonthlyReport = async () => {
    setLoading(true);
    try {
      const year = selectedMonth.split('-')[0];
      const month = selectedMonth.split('-')[1];
      const daysInMonth = new Date(year, month, 0).getDate();
      
      const dailyData = [];
      const projectData = {};
      let totalSeconds = 0;
      let totalActiveSeconds = 0;
      let totalIdleSeconds = 0;
      let totalManualSeconds = 0;
      let workingDays = 0;

      // Fetch data for each day of the month
      for (let day = 1; day <= daysInMonth; day++) {
        const date = `${year}-${month.padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        
        try {
          // Fetch automatic tracking sessions
          const response = await timeTrackingApi.getSessionsByDate(date);
          const sessions = response.data.sessions || [];
          
          // Fetch manual timesheet entries
          const timesheetResponse = await api.get('/timesheets');
          const allEntries = timesheetResponse.data || [];
          const manualEntries = allEntries.filter(entry => entry.date === date);
          
          let dayTotal = 0;
          let dayActive = 0;
          let dayIdle = 0;
          let dayManual = 0;
          let sessionCount = 0;
          let manualCount = manualEntries.length;
          
          // Process automatic sessions
          sessions.forEach(session => {
            dayTotal += session.total_seconds || 0;
            dayActive += session.active_seconds || 0;
            dayIdle += session.idle_seconds || 0;
            sessionCount++;
            
            // Group by project
            const projectKey = session.project_name || 'No Project';
            if (!projectData[projectKey]) {
              projectData[projectKey] = {
                name: projectKey,
                totalSeconds: 0,
                activeSeconds: 0,
                idleSeconds: 0,
                manualSeconds: 0,
                days: 0,
                sessions: 0,
                manualEntries: 0
              };
            }
            projectData[projectKey].totalSeconds += session.total_seconds || 0;
            projectData[projectKey].activeSeconds += session.active_seconds || 0;
            projectData[projectKey].idleSeconds += session.idle_seconds || 0;
            projectData[projectKey].sessions++;
          });
          
          // Process manual entries
          manualEntries.forEach(entry => {
            const manualSeconds = parseFloat(entry.hours) * 3600;
            dayManual += manualSeconds;
            dayTotal += manualSeconds;
            
            // Group by project
            const projectKey = entry.Project?.name || 'No Project';
            if (!projectData[projectKey]) {
              projectData[projectKey] = {
                name: projectKey,
                totalSeconds: 0,
                activeSeconds: 0,
                idleSeconds: 0,
                manualSeconds: 0,
                days: 0,
                sessions: 0,
                manualEntries: 0
              };
            }
            projectData[projectKey].totalSeconds += manualSeconds;
            projectData[projectKey].manualSeconds += manualSeconds;
            projectData[projectKey].manualEntries++;
          });

          if (dayTotal > 0) {
            workingDays++;
            projectData && Object.keys(projectData).forEach(key => {
              const hasAutoWork = sessions.some(s => 
                (s.project_name || 'No Project') === key
              );
              const hasManualWork = manualEntries.some(e => 
                (e.Project?.name || 'No Project') === key
              );
              if (hasAutoWork || hasManualWork) {
                projectData[key].days++;
              }
            });
          }

          totalSeconds += dayTotal;
          totalActiveSeconds += dayActive;
          totalIdleSeconds += dayIdle;
          totalManualSeconds += dayManual;

          dailyData.push({
            date,
            day: new Date(date).toLocaleDateString('en-US', { weekday: 'short' }),
            totalSeconds: dayTotal,
            activeSeconds: dayActive,
            idleSeconds: dayIdle,
            manualSeconds: dayManual,
            sessionCount,
            manualCount,
            productivity: (dayTotal - dayManual) > 0 ? Math.round((dayActive / (dayTotal - dayManual)) * 100) : 0
          });
        } catch (error) {
          console.error(`Error fetching data for ${date}:`, error);
          dailyData.push({
            date,
            day: new Date(date).toLocaleDateString('en-US', { weekday: 'short' }),
            totalSeconds: 0,
            activeSeconds: 0,
            idleSeconds: 0,
            manualSeconds: 0,
            sessionCount: 0,
            manualCount: 0,
            productivity: 0
          });
        }
      }

      setReportData({
        dailyBreakdown: dailyData,
        monthlyTotals: {
          totalDays: daysInMonth,
          workingDays,
          totalHours: Math.round(totalSeconds / 3600 * 100) / 100,
          activeHours: Math.round(totalActiveSeconds / 3600 * 100) / 100,
          idleHours: Math.round(totalIdleSeconds / 3600 * 100) / 100,
          manualHours: Math.round(totalManualSeconds / 3600 * 100) / 100,
          productivity: (totalSeconds - totalManualSeconds) > 0 ? Math.round((totalActiveSeconds / (totalSeconds - totalManualSeconds)) * 100) : 0
        },
        projectWiseData: Object.values(projectData)
      });
    } catch (error) {
      console.error('Error loading monthly report:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatHours = (hours) => {
    return `${hours.toFixed(1)}h`;
  };

  const getProductivityColor = (productivity) => {
    if (productivity >= 80) return 'text-green-600';
    if (productivity >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProductivityBg = (productivity) => {
    if (productivity >= 80) return 'bg-green-100';
    if (productivity >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const maxDailyHours = Math.max(...reportData.dailyBreakdown.map(d => d.totalSeconds / 3600));

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Monthly Time Report</h1>
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <FiCalendar className="mr-2 text-gray-500" />
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading monthly report...</p>
        </div>
      ) : (
        <>
          {/* Monthly Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                  <FiCalendar size={24} />
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500">Working Days</h3>
                  <p className="text-2xl font-semibold text-gray-900">
                    {reportData.monthlyTotals.workingDays}/{reportData.monthlyTotals.totalDays}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-green-100 text-green-600">
                  <FiClock size={24} />
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500">Total Hours</h3>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatHours(reportData.monthlyTotals.totalHours)}
                  </p>
                  <div className="text-xs text-gray-400 space-y-1">
                    <div>Auto: {formatHours(reportData.monthlyTotals.activeHours + reportData.monthlyTotals.idleHours)}</div>
                    <div className="flex items-center">
                      <FiEdit3 size={10} className="mr-1 text-green-500" />
                      Manual: {formatHours(reportData.monthlyTotals.manualHours)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                  <FiTrendingUp size={24} />
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500">Productivity</h3>
                  <p className={`text-2xl font-semibold ${getProductivityColor(reportData.monthlyTotals.productivity)}`}>
                    {reportData.monthlyTotals.productivity}%
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-orange-100 text-orange-600">
                  <FiBarChart2 size={24} />
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500">Avg Daily</h3>
                  <p className="text-2xl font-semibold text-gray-900">
                    {reportData.monthlyTotals.workingDays > 0 
                      ? formatHours(reportData.monthlyTotals.totalHours / reportData.monthlyTotals.workingDays)
                      : '0h'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Daily Breakdown Chart */}
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-6">Daily Time Breakdown</h3>
              <div className="space-y-2">
                {reportData.dailyBreakdown.map((day, index) => (
                  <div key={index} className="flex items-center py-2">
                    <div className="w-20 text-sm font-medium text-gray-600">
                      {day.date.split('-')[2]} {day.day}
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="flex items-center h-8">
                        <div 
                          className="bg-blue-200 h-6 rounded-l flex items-center justify-center text-xs font-medium text-blue-800"
                          style={{ width: `${maxDailyHours > 0 ? (day.activeSeconds / 3600 / maxDailyHours) * 100 : 0}%` }}
                        >
                          {day.activeSeconds > 0 && formatTime(day.activeSeconds)}
                        </div>
                        <div 
                          className="bg-red-200 h-6 flex items-center justify-center text-xs font-medium text-red-800"
                          style={{ width: `${maxDailyHours > 0 ? (day.idleSeconds / 3600 / maxDailyHours) * 100 : 0}%` }}
                        >
                          {day.idleSeconds > 0 && formatTime(day.idleSeconds)}
                        </div>
                        <div 
                          className="bg-green-200 h-6 rounded-r flex items-center justify-center text-xs font-medium text-green-800"
                          style={{ width: `${maxDailyHours > 0 ? (day.manualSeconds / 3600 / maxDailyHours) * 100 : 0}%` }}
                        >
                          {day.manualSeconds > 0 && formatTime(day.manualSeconds)}
                        </div>
                      </div>
                    </div>
                    <div className="w-20 text-right">
                      <div className="text-sm font-medium">{formatTime(day.totalSeconds)}</div>
                      <div className={`text-xs ${getProductivityColor(day.productivity)}`}>
                        {day.productivity}%
                      </div>
                    </div>
                    <div className="w-20 text-right text-sm text-gray-500">
                      <div>{day.sessionCount} auto</div>
                      {day.manualCount > 0 && (
                        <div className="text-green-600">{day.manualCount} manual</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center mt-4 text-xs text-gray-500">
                <div className="flex items-center mr-4">
                  <div className="w-3 h-3 bg-blue-200 rounded mr-1"></div>
                  Active Time
                </div>
                <div className="flex items-center mr-4">
                  <div className="w-3 h-3 bg-red-200 rounded mr-1"></div>
                  Idle Time
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-200 rounded mr-1"></div>
                  Manual Time
                </div>
              </div>
            </div>
          </div>

          {/* Project-wise Breakdown */}
          {reportData.projectWiseData.length > 0 && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-6">Project-wise Summary</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Project
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Active Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Manual Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Productivity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Days Worked
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sessions / Entries
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {reportData.projectWiseData.map((project, index) => {
                        const autoSeconds = project.totalSeconds - (project.manualSeconds || 0);
                        const productivity = autoSeconds > 0 
                          ? Math.round((project.activeSeconds / autoSeconds) * 100) 
                          : 0;
                        
                        return (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {project.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatTime(project.totalSeconds)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatTime(project.activeSeconds)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                              {formatTime(project.manualSeconds || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getProductivityBg(productivity)} ${getProductivityColor(productivity)}`}>
                                {productivity}%
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {project.days} days
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div>{project.sessions} auto</div>
                              {(project.manualEntries || 0) > 0 && (
                                <div className="text-green-600">{project.manualEntries} manual</div>
                              )}
                            </td>
                          </tr>
                        );
                      })}}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MonthlyReportPage;